import { API_CONFIG } from "./saanvi-constants";

export class ChatService {
  constructor() {
    this.currentController = null;
  }

  cancelCurrentRequest() {
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
  }

  async sendStreamedMessage({
    chat,
    deepAnalysis,
    deepDive,
    deeperDive,
    thinkingMode, // NEW
    files,
    addChat,
    setLoading,
    setLoadingMessage,
    setInCommingMessage,
    scrollToBottom,
    showError,
    authToken,
    onProgressUpdate,
  }) {
    this.cancelCurrentRequest();
    this.currentController = new AbortController();

    setLoading(true);
    setInCommingMessage("");
    let inMessage = "";
    let buffer = "";
    let hasFinishReason = false;
    let currentEvent = null;

    // Set loading message based on mode
    if (thinkingMode) {
      setLoadingMessage("Thinking step by step...");
    } else if (deeperDive) {
      setLoadingMessage("Deep diving into the topic...");
    } else if (deepAnalysis) {
      setLoadingMessage("Analysing...");
    } else if (deepDive) {
      setLoadingMessage("Uncovering details...");
    } else {
      setLoadingMessage("Thinking about it...");
    }

    const url = API_CONFIG.baseUrl;

    try {
      // Determine timeout based on mode
      const timeoutMs = thinkingMode
        ? API_CONFIG.timeouts.thinkingMode
        : deeperDive
        ? API_CONFIG.timeouts.deeperDive
        : deepAnalysis || deepDive
        ? API_CONFIG.timeouts.deepAnalysis
        : API_CONFIG.timeouts.standard;

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("TIMEOUT")), timeoutMs)
      );

      // Prepare request body
      let requestBody;
      let requestHeaders = {
        Authorization: authToken ? `Bearer ${authToken}` : "",
      };

      // Use FormData if files are present
      if (files && files.length > 0) {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("messages", JSON.stringify(chat));
        formData.append("deepAnalysis", String(deepAnalysis));
        formData.append("deepDive", String(deepDive));
        formData.append("deeperDive", String(deeperDive));
        formData.append("thinkingMode", String(thinkingMode)); // NEW
        requestBody = formData;
      } else {
        requestHeaders["Content-Type"] = "application/json";
        requestBody = JSON.stringify({
          messages: chat,
          deepAnalysis,
          deepDive,
          deeperDive,
          thinkingMode, // NEW
        });
      }

      const fetchPromise = fetch(url, {
        method: "POST",
        body: requestBody,
        headers: requestHeaders,
        signal: this.currentController.signal,
      });

      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (!response.ok) {
        let errorData;
        let isServerError = true;

        try {
          errorData = await response.json();
        } catch {
          try {
            errorData = await response.text();
          } catch {
            errorData = { message: `Server error (${response.status})` };
          }
        }

        let errorMessage = "Connection failed";

        if (typeof errorData === "object" && errorData !== null) {
          errorMessage =
            errorData.message || errorData.error || "Server error occurred";

          switch (errorData.errorCode) {
            case "SAANVI-LIMIT-EXCEEDED":
              errorMessage =
                "Anonymous chat limit exceeded. Please login to continue.";
              break;
            case "SAANVI-PROCESSING-ERROR":
              errorMessage = "Failed to process request. Please try again.";
              break;
            case "FILE_TOO_LARGE":
              errorMessage = "File size exceeds 20MB limit.";
              break;
            case "TOO_MANY_FILES":
              errorMessage = "Too many files. Maximum 10 files allowed.";
              break;
            case "INVALID_FILE_TYPE":
              errorMessage =
                "Invalid file type. Only images (jpg, png, gif, webp) and PDFs are supported.";
              break;
            case "FILE_PROCESSING_ERROR":
              errorMessage =
                "Failed to process uploaded files. Please try again.";
              break;
            default:
              break;
          }
        } else if (typeof errorData === "string") {
          try {
            const parsed = JSON.parse(errorData);
            errorMessage = parsed.message || parsed.error || errorData;
          } catch {
            errorMessage = errorData;
          }
        }

        showError(errorMessage, isServerError);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let firstChunkReceived = false;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          if (inMessage.trim()) {
            addChat({ role: "assistant", content: inMessage });
          }
          setInCommingMessage("");
          break;
        }

        if (
          !firstChunkReceived &&
          (deepAnalysis || deepDive || deeperDive || thinkingMode)
        ) {
          setLoadingMessage("Thinking about it...");
          firstChunkReceived = true;
        }

        const chunkText = decoder.decode(value, { stream: true });
        buffer += chunkText;

        // Process line by line instead of event by event
        let lineEnd;
        while ((lineEnd = buffer.indexOf("\n")) !== -1) {
          const line = buffer.substring(0, lineEnd).trim();
          buffer = buffer.substring(lineEnd + 1);

          if (!line) continue;
          if (line === "data: [DONE]") {
            if (inMessage.trim()) {
              addChat({ role: "assistant", content: inMessage });
              setInCommingMessage("");
            }
            return;
          }

          if (line.startsWith("event: ")) {
            currentEvent = line.substring(7).trim();
            continue;
          }

          if (line.startsWith("data: ")) {
            const dataContent = line.substring(6).trim();

            try {
              const parsed = JSON.parse(dataContent);

              // Progress events
              if (currentEvent === "progress" && onProgressUpdate) {
                onProgressUpdate(parsed);
                currentEvent = null;
                continue;
              }

              // Thinking mode
              if (parsed.type === "thinking" || parsed.type === "working") {
                if (onProgressUpdate) onProgressUpdate(parsed);
                continue;
              }

              // Message content
              const content = parsed?.choices?.[0]?.delta?.content;
              if (content) {
                inMessage += content;
                setInCommingMessage((prev) => prev + content);
                scrollToBottom();
                requestAnimationFrame(() => scrollToBottom());
              }

              if (parsed?.choices?.[0]?.finish_reason) {
                hasFinishReason = true;
              }
            } catch (e) {
              console.log("Parse error:", e, dataContent.substring(0, 100));
            }

            currentEvent = null;
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request cancelled by user");
        return;
      }

      let errorMessage = "An unexpected error occurred. Please try again.";
      let isServerError = false;

      if (error.message === "TIMEOUT") {
        errorMessage =
          deepAnalysis || deepDive || deeperDive || thinkingMode
            ? "Analysis is taking longer than expected. The server might be busy. Please try again."
            : "Response timeout. Please try again.";
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        errorMessage = "No internet connection. Please check your network.";
      }

      showError(errorMessage, isServerError);
      console.error("Streaming error:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("Thinking about it...");
      this.currentController = null;
    }
  }
}

export const chatService = new ChatService();
